import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import useCurrentPlan from "./useCurrentPlan";
import { useAppSelector } from "../app/hooks";
import type { GetDataConfidenceRequest } from "../contracts";
import { getToolResponseById, isCommercialSimulatorSubBrandToolResponse, isCommercialSimulatorToolResponse } from "../models/toolResponseModels";
import { selectMessages } from "../slices/chatSlice";

export const useDataConfidenceRequest = (): GetDataConfidenceRequest | typeof skipToken => {
    const { currentPlan, planStartDate, planEndDate } = useCurrentPlan();
    const messages = useAppSelector(selectMessages);

    const toolResponse = getToolResponseById(messages, currentPlan?.tool_use_id);

    return useMemo(
        () => {
            if (!currentPlan || !toolResponse) {
                return skipToken;
            }
            const fromDate = new Date();
            const toDate = new Date();
            fromDate.setMonth(fromDate.getMonth() - 3);
            const hasPlanDates = planStartDate && planEndDate
                && !Number.isNaN(Date.parse(planStartDate)) && !Number.isNaN(Date.parse(planEndDate))
                && Date.parse(planEndDate) >= Date.parse(planStartDate);

            const requestFromDate = hasPlanDates ? planStartDate : fromDate.toISOString();
            const requestToDate = hasPlanDates ? planEndDate : toDate.toISOString();

            if (isCommercialSimulatorToolResponse(toolResponse)) {
                return {
                    product_ids: [toolResponse.tool_call_user_selection.sku_id],
                    retailer_id: toolResponse.tool_call_user_selection.retailer_id,
                    from_date: requestFromDate,
                    to_date: requestToDate
                };
            } else if (isCommercialSimulatorSubBrandToolResponse(toolResponse)) {
                if (!toolResponse.tool_call_user_selection.products || toolResponse.tool_call_user_selection.products.length === 0) {
                    return skipToken;
                }

                return {
                    product_ids: toolResponse.tool_call_user_selection.products.map(o => o.id),
                    retailer_id: toolResponse.tool_call_user_selection.products[0].retailer_id,
                    from_date: requestFromDate,
                    to_date: requestToDate
                };
            }

            return skipToken;
        },
        [currentPlan, toolResponse, planStartDate, planEndDate]
    );
};
